/**
 * Nextcloud Settings connect. Login Flow v2 opens on the instance.
 * The app password is stored by the API and never shown here.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SettingsSection } from '@/components/features/settings/settings-section'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'

interface NextcloudIntegrationSectionProps {
  organizationId?: string | null
  canManage?: boolean
  enabled?: boolean
  onSaveConfig?: (config: Record<string, string>) => Promise<void>
}

interface NextcloudStatus {
  enabled: boolean
  connected: boolean
  loginName: string | null
  instanceUrl: string
  version: string | null
  defaultRoot: string
  importFolder: string
}

function instanceHost(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

function orgQs(organizationId?: string | null): string {
  if (!organizationId) return ''
  return `?organizationId=${encodeURIComponent(organizationId)}`
}

export function NextcloudIntegrationSection({
  organizationId,
  canManage = false,
  enabled = false,
  onSaveConfig,
}: NextcloudIntegrationSectionProps) {
  const [loading, setLoading] = useState(true)
  const [connected, setConnected] = useState(false)
  const [loginName, setLoginName] = useState<string | null>(null)
  const [version, setVersion] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [instanceUrl, setInstanceUrl] = useState('')
  const [defaultRoot, setDefaultRoot] = useState('')
  const [importFolder, setImportFolder] = useState('')
  const [saving, setSaving] = useState(false)
  const [signingIn, setSigningIn] = useState(false)
  const [testing, setTesting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    if (!organizationId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetchWithAuth(`/api/nextcloud/status${orgQs(organizationId)}`)
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Could not load Nextcloud status')
        return
      }
      const status = data as NextcloudStatus
      setConnected(Boolean(status.connected))
      setLoginName(status.loginName)
      setVersion(typeof status.version === 'string' ? status.version : null)
      setInstanceUrl(status.instanceUrl || '')
      setDefaultRoot(status.defaultRoot || '')
      setImportFolder(status.importFolder || '')
    } catch {
      setError('Could not load Nextcloud status')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current)
    }
  }, [])

  async function saveSettings() {
    if (!onSaveConfig) return
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await onSaveConfig({
        instanceUrl: instanceUrl.trim(),
        defaultRoot: defaultRoot.trim(),
        importFolder: importFolder.trim(),
      })
      setEditing(false)
      setMessage('Saved instance settings.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save settings')
    } finally {
      setSaving(false)
    }
  }

  function stopPolling() {
    if (pollTimer.current) clearTimeout(pollTimer.current)
    pollTimer.current = null
    setSigningIn(false)
  }

  async function pollOnce(pollId: string, attemptsLeft: number) {
    if (!organizationId) return
    try {
      const res = await fetchWithAuth(`/api/nextcloud/login/poll${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId, pollId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Nextcloud sign-in failed')
        stopPolling()
        return
      }
      if (data.pending) {
        if (attemptsLeft <= 0) {
          setError('Sign-in timed out. Start again.')
          stopPolling()
          return
        }
        pollTimer.current = setTimeout(() => {
          void pollOnce(pollId, attemptsLeft - 1)
        }, 3000)
        return
      }
      setConnected(true)
      setLoginName(typeof data.loginName === 'string' ? data.loginName : null)
      setVersion(typeof data.version === 'string' ? data.version : null)
      if (typeof data.instanceUrl === 'string') setInstanceUrl(data.instanceUrl)
      setEditing(false)
      setMessage(null)
      stopPolling()
    } catch {
      setError('Nextcloud sign-in failed')
      stopPolling()
    }
  }

  async function signIn() {
    if (!organizationId || !enabled) return
    setSigningIn(true)
    setError(null)
    setMessage(null)
    try {
      const res = await fetchWithAuth(`/api/nextcloud/login/start${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId, instanceUrl: instanceUrl.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Could not start sign-in')
        setSigningIn(false)
        return
      }
      if (typeof data.loginUrl === 'string') {
        window.open(data.loginUrl, '_blank', 'noopener,noreferrer')
      }
      setMessage('Finish sign-in in the Nextcloud window. This page will update.')
      void pollOnce(String(data.pollId), 40)
    } catch {
      setError('Could not start sign-in')
      setSigningIn(false)
    }
  }

  async function disconnect() {
    if (!organizationId) return
    setError(null)
    setMessage(null)
    try {
      const res = await fetchWithAuth(`/api/nextcloud/logout${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Could not disconnect')
        return
      }
      setConnected(false)
      setLoginName(null)
      setVersion(null)
      setEditing(false)
      setMessage('Disconnected.')
    } catch {
      setError('Could not disconnect')
    }
  }

  async function testConnection() {
    if (!organizationId) return
    setTesting(true)
    setError(null)
    setMessage(null)
    try {
      const res = await fetchWithAuth(`/api/nextcloud/test${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Connection test failed')
        return
      }
      if (typeof data.version === 'string' && data.version) setVersion(data.version)
      setMessage(null)
    } catch {
      setError('Connection test failed')
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading Nextcloud...
      </div>
    )
  }

  const settingsFields = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="nc-settings-url">Instance URL</Label>
        <Input
          id="nc-settings-url"
          type="url"
          placeholder="https://cloud.example.org"
          value={instanceUrl}
          onChange={(e) => setInstanceUrl(e.target.value)}
          disabled={!canManage}
          data-testid={testIds.nextcloud.instanceUrlInput}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="nc-settings-root">Default browse root</Label>
        <Input
          id="nc-settings-root"
          type="text"
          placeholder="/Maater"
          value={defaultRoot}
          onChange={(e) => setDefaultRoot(e.target.value)}
          disabled={!canManage}
          data-testid={testIds.nextcloud.defaultRootInput}
        />
        <p className="text-xs text-muted-foreground">
          The file list opens here. The sidebar still shows the rest of the account.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="nc-settings-import">Import into</Label>
        <Input
          id="nc-settings-import"
          type="text"
          placeholder="workspace/inbox/nextcloud"
          value={importFolder}
          onChange={(e) => setImportFolder(e.target.value)}
          disabled={!canManage}
          data-testid={testIds.nextcloud.importFolderInput}
        />
      </div>
      {canManage && (
        <Button type="button" variant="outline" onClick={() => void saveSettings()} disabled={saving}>
          {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          Save settings
        </Button>
      )}
    </div>
  )

  const connectedBadge = (
    <Badge
      variant="outline"
      className="border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400 gap-1.5 px-2.5 py-1 text-xs font-medium"
    >
      <Check className="h-3.5 w-3.5 shrink-0" />
      Connected
    </Badge>
  )

  return (
    <SettingsSection
      title={connected ? loginName || 'Account' : 'Instance'}
      badge={connected ? connectedBadge : undefined}
      description={
        connected
          ? `${instanceHost(instanceUrl)}${version ? ` · Nextcloud ${version}` : ''}`
          : 'Sign in with Login Flow. Maater stores the app password for this user and does not show it again.'
      }
      actions={
        connected ? (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void testConnection()}
              disabled={testing}
              data-testid={testIds.nextcloud.testConnectionButton}
            >
              {testing ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Test connection
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void disconnect()}
              data-testid={testIds.nextcloud.disconnectButton}
            >
              Disconnect
            </Button>
          </div>
        ) : undefined
      }
      data-testid={testIds.nextcloud.settingsShell}
    >
      {connected ? (
        <div className="space-y-5">
          <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2 border-t border-border pt-4 text-sm">
            <dt className="text-muted-foreground">Instance</dt>
            <dd className="min-w-0 truncate">{instanceUrl}</dd>
            <dt className="text-muted-foreground">Browse root</dt>
            <dd className="min-w-0 truncate">{defaultRoot || 'Home'}</dd>
            <dt className="text-muted-foreground">Import into</dt>
            <dd className="min-w-0 truncate">{importFolder || 'Not set'}</dd>
          </dl>
          {canManage && (
            <div className="border-t border-border pt-4">
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => setEditing((open) => !open)}
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${editing ? 'rotate-180' : ''}`} />
                Edit instance
              </button>
              {editing ? <div className="mt-4">{settingsFields}</div> : null}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {settingsFields}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={() => void signIn()}
              disabled={!enabled || signingIn || !instanceUrl.trim()}
              data-testid={testIds.nextcloud.connectButton}
            >
              {signingIn ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Sign in
            </Button>
          </div>
          {!enabled && (
            <p className="text-xs text-muted-foreground">Enable Nextcloud before signing in.</p>
          )}
        </div>
      )}
      {message ? <p className="text-xs text-muted-foreground">{message}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </SettingsSection>
  )
}
